import React from 'react';
import { Calculator, Settings, PiggyBank } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров досрочного погашения
 */
const QuickSettingsSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущей суммы кредита
  const loanAmount = parseFloat(formData.loanAmount) || 0;
  
  // Расчет текущего срока в месяцах
  const loanTermMonths = Math.round((parseFloat(formData.loanTerm) || 0) * 12);
  
  // Обработчик изменения суммы досрочного погашения
  const handleEarlyRepaymentAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      earlyRepaymentAmount: newValue
    }));
  };
  
  // Обработчик изменения месяца начала досрочного погашения
  const handleEarlyRepaymentStartMonthChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      earlyRepaymentStartMonth: newValue
    }));
  };
  
  // Обработчик изменения процентной ставки
  const handleInterestRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      interestRate: newValue
    }));
  };
  
  // Предопределенные суммы досрочного погашения для быстрого выбора
  const predefinedAmounts = [
    { value: (loanAmount * 0.05).toFixed(0), label: '5% от суммы' },
    { value: (loanAmount * 0.1).toFixed(0), label: '10% от суммы' },
    { value: (loanAmount * 0.2).toFixed(0), label: '20% от суммы' },
    { value: (loanAmount * 0.5).toFixed(0), label: '50% от суммы' }
  ];
  
  // Предопределенные месяцы начала досрочного погашения
  const currentPaymentsMade = parseInt(formData.paymentsMade) || 0;
  const nextMonth = currentPaymentsMade + 1;
  
  const predefinedStartMonths = [
    { value: nextMonth.toString(), label: 'Следующий платеж' },
    { value: (nextMonth + 2).toString(), label: 'Через 3 месяца' },
    { value: (nextMonth + 5).toString(), label: 'Через 6 месяцев' },
    { value: (nextMonth + 11).toString(), label: 'Через 1 год' }
  ];
  
  // Функция расчета
  const calculateEarlyRepayment = () => {
    // Вызов основной функции расчета (находится в родительском компоненте)
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-green-500" />
        Параметры досрочного погашения
      </h3>
      
      <div className="space-y-5">
        {/* Сумма досрочного погашения */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="earlyRepaymentAmountSlider" className="text-gray-600 dark:text-gray-400">Сумма досрочного погашения</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(parseFloat(formData.earlyRepaymentAmount) || 0, 0)}</span>
          </div>
          <input
            type="range"
            id="earlyRepaymentAmountSlider"
            min="10000"
            max={Math.min(loanAmount * 0.9, 5000000)}
            step="10000"
            value={parseFloat(formData.earlyRepaymentAmount) || 0}
            onChange={handleEarlyRepaymentAmountChange}
            aria-label="Сумма досрочного погашения"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-2 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, earlyRepaymentAmount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.earlyRepaymentAmount === amount.value 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Месяц начала досрочного погашения */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="earlyRepaymentStartMonthSlider" className="text-gray-600 dark:text-gray-400">Начать с месяца</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.earlyRepaymentStartMonth} 
              {parseInt(formData.earlyRepaymentStartMonth) === nextMonth ? ' (следующий)' : ''}
            </span>
          </div>
          <input
            type="range"
            id="earlyRepaymentStartMonthSlider"
            min={nextMonth}
            max={Math.min(loanTermMonths - 1, nextMonth + 24)}
            step="1"
            value={parseInt(formData.earlyRepaymentStartMonth) || nextMonth}
            onChange={handleEarlyRepaymentStartMonthChange}
            aria-label="Месяц начала досрочного погашения"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными месяцами */}
          <div className="grid grid-cols-2 gap-1 mt-2">
            {predefinedStartMonths.map(month => (
              <button
                key={month.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, earlyRepaymentStartMonth: month.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.earlyRepaymentStartMonth === month.value 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {month.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип досрочного погашения */}
        <div>
          <div className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
            Цель досрочного погашения
          </div>
          <div className="grid grid-cols-1 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, earlyRepaymentType: 'reduction' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.earlyRepaymentType === 'reduction' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Уменьшение срока</div>
              <div className="text-xs opacity-75">Платеж без изменений</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, earlyRepaymentType: 'payment' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.earlyRepaymentType === 'payment' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Уменьшение платежа</div>
              <div className="text-xs opacity-75">Срок не меняется</div>
            </button>
          </div>
        </div>
        
        {/* Частота досрочных платежей */}
        <div>
          <div className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
            Периодичность досрочных платежей
          </div>
          <div className="grid grid-cols-2 gap-1">
            {[
              { id: 'once', label: 'Разовый' },
              { id: 'monthly', label: 'Ежемесячно' },
              { id: 'quarterly', label: 'Ежеквартально' },
              { id: 'annually', label: 'Ежегодно' },
            ].map(freq => (
              <button
                key={freq.id}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, earlyRepaymentFrequency: freq.id }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.earlyRepaymentFrequency === freq.id 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {freq.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateEarlyRepayment}
          icon={<PiggyBank className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать досрочное погашение
        </Button>
      </div>
    </div>
  );
};

export default QuickSettingsSidebar;